import React, { useEffect, useMemo, useState, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useArticles from "../../../hooks/useArticle"; // ⚠️ adapte si ton hook s'appelle useArticles
import useCategorie from "../../../hooks/useCategorie";
import ArticleUpdateModal from "../../../components/article/ArticleUpdateModal"; // ⚠️ adapte le chemin réel
import { AuthContext } from "../../../contexts/AuthContext";

const MAX_PHOTOS = 10;

const ArticleUpdatePage = ({ setAlert }) => {
  const { id } = useParams();
  const navigate = useNavigate();

  const { user: connectedUser } = useContext(AuthContext);

  const { findOneArticle, updateArticle, formArticle, setFormArticle } = useArticles();
  const { getAllCategories } = useCategorie();

  const [show, setShow] = useState(true);
  const [loading, setLoading] = useState(true);
  const [updateLoading, setUpdateLoading] = useState(false);

  const [categories, setCategories] = useState([]);
  const [infoCle, setInfoCle] = useState([]);

  const goBack = () => {
    if (Number(connectedUser?.role) === 1) {
      navigate("/admin/articles");
    } else {
      navigate("/utilisateur/compte");
    }
  };

  // Remplir le form à partir de l'article
  const hydrateFormFromArticle = (articleData) => {
    // articleData.photos = ["x.jpg", ...]
    // articleData.idCategorie peut être un objet (aggregate) ou id
    const catId =
      articleData?.idCategorie?._id ||
      articleData?.categorie?._id ||
      articleData?.idCategorie ||
      "";

    setFormArticle((prev) => ({
      ...prev,
      _id: articleData?._id,
      titre: articleData?.titre || "",
      description: articleData?.description || "",
      prix: articleData?.prix ?? "",
      ville: articleData?.ville || "",
      quartier: articleData?.quartier || "",
      phone: articleData?.phone || "",
      etat: Number(articleData?.etat || 2),
      typeOffre: Number(articleData?.typeOffre || 1),
      typeAbonnement: Number(articleData?.typeAbonnement || 2),
      status: Number(articleData?.status || 1),
      archive: Number(articleData?.archive || 2),
      idPublicationFacebook: articleData?.idPublicationFacebook || "",

      idCategorie: catId,

      // important pour ton modal :
      oldPhotos: Array.isArray(articleData?.photos) ? articleData.photos : [],
      photos: [], // nouvelles photos (File[])
    }));

    setInfoCle(Array.isArray(articleData?.infoCle) ? articleData.infoCle : []);
  };

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      if (!id) {
        setAlert?.({ type: "danger", message: "ID article manquant." });
        goBack();
        return;
      }

      setLoading(true);
      try {
        // catégories
        const catRes = await getAllCategories();
        const cats = catRes?.data?.data || catRes?.data || [];
        if (mounted) setCategories(Array.isArray(cats) ? cats : []);

        // article
        const res = await findOneArticle(id);
        const data = res?.data?.data;
        if (!data) throw new Error("Article introuvable");
        if (mounted) hydrateFormFromArticle(data);
      } catch (e) {
        console.error(e);
        setAlert?.({ type: "danger", message: "Impossible de charger l’article." });
        goBack();
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // handlers demandés par ton modal
  const onChange = (e) => {
    const { name, value } = e.target;
    setFormArticle((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const onPhotosChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    setFormArticle((prev) => {
      const currentNew = Array.isArray(prev.photos) ? prev.photos : [];
      const currentOld = Array.isArray(prev.oldPhotos) ? prev.oldPhotos : [];
      const totalNow = currentOld.length + currentNew.length;

      const canTake = Math.max(0, MAX_PHOTOS - totalNow);
      const toAdd = files.slice(0, canTake);

      return { ...prev, photos: [...currentNew, ...toAdd] };
    });

    // reset input pour pouvoir re-choisir le même fichier
    e.target.value = "";
  };

  const onRemoveOldPhoto = (index) => {
    setFormArticle((prev) => ({
      ...prev,
      oldPhotos: (prev.oldPhotos || []).filter((_, i) => i !== index),
    }));
  };

  const onRemoveNewPhoto = (index) => {
    setFormArticle((prev) => ({
      ...prev,
      photos: (prev.photos || []).filter((_, i) => i !== index),
    }));
  };

  const remainingPhotos = useMemo(() => {
    const oldCount = formArticle?.oldPhotos?.length || 0;
    const newCount = formArticle?.photos?.length || 0;
    return Math.max(0, MAX_PHOTOS - (oldCount + newCount));
  }, [formArticle?.oldPhotos?.length, formArticle?.photos?.length]);

  const onConfirm = async () => {
    try {
      if (!id) return;

      setUpdateLoading(true);

      // Construire FormData (comme ton create/update existant)
      const fd = new FormData();

      // Champs simples : on évite photos/oldPhotos ici
      Object.keys(formArticle || {}).forEach((key) => {
        if (key === "photos" || key === "oldPhotos") return;
        const val = formArticle[key];
        if (val !== undefined && val !== null && val !== "") {
          fd.append(key, val);
        }
      });

      // infoCle (backend attend string JSON)
      fd.append("infoCle", JSON.stringify(infoCle || []));

      // nouvelles photos (File[])
      (formArticle?.photos || []).forEach((file) => fd.append("photos", file));

      // anciennes photos conservées
      fd.append("existingPhotos", JSON.stringify(formArticle?.oldPhotos || []));

      await updateArticle(id, fd)
      .then((res) => {})
      .catch((err) => console.log(err));;

      setAlert?.({ type: "success", message: "Article modifié avec succès." });

      // 🔥 règle demandée :
      // admin => retour admin articles
      // vendeur/acheteur => retour dashboard
      goBack();
    } catch (e) {
      console.error(e);
      setAlert?.({ type: "danger", message: "Échec de la modification de l’article." });
    } finally {
      setUpdateLoading(false);
    }
  };

  const onClose = () => {
    if (updateLoading) return;
    goBack();
  };

  // Tant que ça charge, on peut afficher le modal mais désactiver / ou afficher un loader
  // Ici : on affiche quand même, mais tu peux aussi afficher un loader full page.
  return (
    <ArticleUpdateModal
      show={show}
      updateLoading={updateLoading || loading}
      formArticle={formArticle}
      setFormArticle={setFormArticle}
      infoCle={infoCle}
      categories={categories}
      maxPhotos={MAX_PHOTOS}
      remainingPhotos={remainingPhotos}
      onChange={onChange}
      onPhotosChange={onPhotosChange}
      onRemoveOldPhoto={onRemoveOldPhoto}
      onRemoveNewPhoto={onRemoveNewPhoto}
      onClose={onClose}
      onConfirm={onConfirm}
      setInfoCle={setInfoCle}
    />
  );
};

export default ArticleUpdatePage;
